import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SButton } from '@/components/ui/atoms/SButton';
import { useNativeLocationTracking, LocationTrackingStatus } from '@/hooks/useNativeLocationTracking';
import { useBookings } from '@/hooks/useBookings';
import { GeoLocation } from '@/types/nativeBridge.types';
import { CHECK_IN_DISTANCE_THRESHOLD_YARDS } from '@/constants/location';
import './LocationVerification.css';
import { ArrowLeft, MapPin } from 'phosphor-react';

interface LocationState {
  bookingId: string;
  targetLocation: GeoLocation;
  facilityName?: string;
  /** Resolved Box Pro contact, surfaced as the "Contact us" tel:/mailto: link. */
  contact?: {
    type: 'tel' | 'mailto';
    value: string;
  };
  /**
   * What to do once the member is verified within range.
   *  - 'checkIn' (default): call the check-in API, then navigate to OpenGate.
   *  - 'openDoor': member is already checked in — skip check-in and go straight
   *    to OpenGate. Uses a more generous distance threshold.
   */
  purpose?: 'checkIn' | 'openDoor';
}

// sessionStorage key used to survive the "Retry" full page reload.
const LOCATION_VERIFICATION_STATE_KEY = 'locationVerificationState';

/**
 * LocationVerification Page
 * Verifies user is within range of the golf course before allowing check-in
 * Shows "too far away" screen if user is not within 50 yards
 */
const LocationVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve the booking nav state. A "Retry" does a real page reload, which
  // wipes router state — so we mirror it into sessionStorage and fall back to
  // that on reload, otherwise the page would bounce to /dashboard.
  const navState = location.state as LocationState | null;
  const state: LocationState | null = useMemo(() => {
    if (navState?.targetLocation) {
      return navState;
    }
    try {
      const cached = sessionStorage.getItem(LOCATION_VERIFICATION_STATE_KEY);
      return cached ? (JSON.parse(cached) as LocationState) : null;
    } catch {
      return null;
    }
  }, [navState]);

  // Mirror fresh nav state into sessionStorage so the "Retry" full reload
  // (which wipes router state) can rehydrate instead of bouncing to /dashboard.
  useEffect(() => {
    if (navState?.targetLocation) {
      try {
        sessionStorage.setItem(
          LOCATION_VERIFICATION_STATE_KEY,
          JSON.stringify(navState)
        );
      } catch {
        /* sessionStorage unavailable — non-fatal */
      }
    }
  }, [navState]);

  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // Check-in and Open Door both gate on the same physical radius.
  const purpose = state?.purpose ?? 'checkIn';
  const distanceThreshold = CHECK_IN_DISTANCE_THRESHOLD_YARDS;

  const {
    status,
    distance,
    withinRange,
    error,
    locationUpdate,
    startTracking,
    stopTracking
  } = useNativeLocationTracking();

  const { checkInBooking } = useBookings();

  // Debug logging for status and location updates
  useEffect(() => {
    const logMsg = `[LocationVerification] Status: ${status}, Distance: ${distance}, WithinRange: ${withinRange}`;
    console.log(logMsg);
    console.log('[LocationVerification] LocationUpdate:', locationUpdate);
    
    // Send to native for debugging
    if ((window as any).ReactNativeWebView) {
      try {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'web_log',
          payload: { message: logMsg, data: { status, distance, withinRange } }
        }));
      } catch {}
    }
  }, [status, locationUpdate, distance, withinRange]);

  // Start tracking when component mounts
  useEffect(() => {
    if (!state?.targetLocation) {
      console.error('[LocationVerification] No target location provided');
      navigate('/home', { replace: true });
      return;
    }

    startTracking(state.targetLocation, distanceThreshold);

    return () => {
      console.log('[LocationVerification] Component unmounting, stopping tracking');
      stopTracking();
      setIsCheckingIn(false); // Reset checking in state
    };
  }, [state?.targetLocation, startTracking, stopTracking, distanceThreshold]);

  // When within range, run the verified action and navigate to OpenGate.
  // - purpose 'checkIn' (default): call the check-in API, then navigate.
  // - purpose 'openDoor': member is already checked in — skip the API and
  //   navigate straight to OpenGate.
  useEffect(() => {
    if (!state) return;
    const isWithinThreshold = distance !== null && distance <= distanceThreshold;

    if (isWithinThreshold && !isCheckingIn) {
      console.log(`[LocationVerification] Within range (${distance} <= ${distanceThreshold} yards), purpose=${purpose}`);
      setIsCheckingIn(true);
      stopTracking();

      const clearCachedState = () => {
        try {
          sessionStorage.removeItem(LOCATION_VERIFICATION_STATE_KEY);
        } catch {
          /* non-fatal */
        }
      };

      const goToOpenGate = () => {
        clearCachedState();
        navigate('/access-control/open-gate', {
          state: {
            bookingId: state.bookingId,
            facilityName: state.facilityName,
          },
          replace: true,
        });
      };

      if (purpose === 'openDoor') {
        // Already checked in — just open the door.
        goToOpenGate();
        return;
      }

      // Default: check-in flow.
      checkInBooking(state.bookingId, () => {
        console.log('[LocationVerification] Check-in successful, navigating to OpenGate');
        goToOpenGate();
      });
    }
  }, [distance, isCheckingIn, navigate, state?.bookingId, stopTracking, checkInBooking, distanceThreshold, purpose]);

  const handleBack = () => {
    stopTracking();
    // Explicit leave — drop the cached state so a later entry starts fresh.
    try {
      sessionStorage.removeItem(LOCATION_VERIFICATION_STATE_KEY);
    } catch {
      /* non-fatal */
    }
    navigate('/home', { replace: true });
  };

  // Retry by actually reloading the page. The booking nav state is preserved
  // via sessionStorage (see `state` above), so the reloaded page re-runs the
  // full location check from scratch instead of getting stuck.
  const handleRetry = () => {
    window.location.reload();
  };

  // Resolved Box Pro contact (phone or email) configured in Box OS.
  // Build the link target: tel: numbers are sanitized to digits with an
  // optional leading +, mailto: addresses are used as-is.
  const contact = state?.contact;
  const contactHref =
    contact && contact.value?.trim()
      ? contact.type === 'tel'
        ? `tel:${contact.value.trim().replace(/(?!^\+)[^\d]/g, '')}`
        : `mailto:${contact.value.trim()}`
      : '';


  // No booking context (e.g. opened directly without nav/session state) —
  // the mount effect redirects to /dashboard; render a placeholder meanwhile.
  if (!state) {
    return (
      <div className="location-verification-container">
        <div className="location-verification-content loading-state">
          <div className="loading-spinner" />
          <p className="loading-text">Verifying your location...</p>
        </div>
      </div>
    );
  }

  // Loading state - show only while starting tracking
  if (status === LocationTrackingStatus.IDLE || status === LocationTrackingStatus.STARTING) {
    return (
      <div className="location-verification-container">
        <div className="lv-loading-header">
          <button className="lv-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} weight="bold" />
          </button>
        </div>
        <div className="location-verification-content loading-state">
          <div className="loading-spinner" />
          <p className="loading-text">Verifying your location...</p>
        </div>
      </div>
    );
  }

  // Tracking state - show loading while waiting for first location update
  if (status === LocationTrackingStatus.TRACKING && !locationUpdate) {
    return (
      <div className="location-verification-container">
        <div className="lv-loading-header">
          <button className="lv-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} weight="bold" />
          </button>
        </div>
        <div className="location-verification-content loading-state">
          <div className="loading-spinner" />
          <p className="loading-text">Getting your location...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === LocationTrackingStatus.ERROR || error) {
    return (
      <div className="location-verification-container">
        <div className="lv-loading-header">
          <button className="lv-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} weight="bold" />
          </button>
        </div>
        <div className="lv-error">
          <div className="lv-error-icon-wrap">
            <MapPin size={36} weight="duotone" color="#c99535" />
          </div>
          <h1 className="lv-error-title">Location Access Denied</h1>
          <p className="lv-error-body">
            Please enable location access in your device settings.
          </p>
          <button className="lv-error-btn lv-error-btn--primary" onClick={handleBack}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Out of range state - main "too far away" screen
  // Show this for OUT_OF_RANGE, STOPPED with out-of-range data, or TRACKING with out-of-range data
  if (status === LocationTrackingStatus.OUT_OF_RANGE || 
      (locationUpdate && !locationUpdate.withinRange)) {
    return (
      <div className="location-verification-container">
        {/* Full Background Map */}
        <div className="map-container">
          <iframe
            className="google-map"
            title="Location Map"
            src={`https://www.google.com/maps/embed/v1/directions?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8'}&origin=${locationUpdate?.currentLocation?.latitude || state.targetLocation.latitude},${locationUpdate?.currentLocation?.longitude || state.targetLocation.longitude}&destination=${state.targetLocation.latitude},${state.targetLocation.longitude}&mode=walking&zoom=16`}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            // Map is reference-only here; disabling pointer events stops the
            // Android WebView iframe from intercepting taps meant for the
            // overlay controls (Back / Contact us).
            style={{ pointerEvents: 'none' }}
          />
        </div>

        {/* Bottom White Overlay */}
        <div className="bottom-overlay">
          {/* Message */}
          <h1 className="verification-title">You are {distance !== null ? Math.round(distance) : '--'} yards away</h1>
          <p className="verification-subtitle">Get within {distanceThreshold} yards</p>
          
          

          {/* Retry link — for when the user has moved closer to the facility */}
          <p className="contact-support-text">
            <button
              type="button"
              className="contact-support-link retry-link"
              onClick={handleRetry}
            >
              Retry
            </button>
          </p>

          {/* Back button */}
          <div className="button-container">
            <SButton
              variant="primary"
              size="lg"
              label="Back"
              onClick={handleBack}
              className="back-button-cta"
            />
          </div>

          {/* Support link — only shown when a contact is configured.
              A real <a href> (not a button + window.location) so Android
              WebView routes tel:/mailto: through the native URL handler. */}
          {contactHref && (
            <p className="contact-support-text">
              Experiencing Issues?{' '}
              <a className="contact-support-link" href={contactHref}>
                Contact us
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default: Still tracking, waiting for location data
  return (
    <div className="location-verification-container">
      <div className="lv-loading-header">
        <button className="lv-back-btn" onClick={handleBack}>
          <ArrowLeft size={20} weight="bold" />
        </button>
      </div>
      <div className="location-verification-content loading-state">
        <div className="loading-spinner" />
        <p className="loading-text">Verifying your location...</p>
      </div>
    </div>
  );
};

export default LocationVerification;
